import React, { useState, useEffect, useCallback, useRef, createContext, useContext } from 'react';
import { createPortal } from 'react-dom';

/**
 * GenApps SharedModal - Componente Universal de Modal
 *
 * BEM prefix: sharemodal
 * Classes: sharemodal-overlay, sharemodal__header, sharemodal--form, etc.
 *
 * NAO conflita com modais locais das GenApps (genmkt-modal, genadm-modal, etc.)
 *
 * Regras: 08.6.MODAL.md
 * Ficheiro: /mnt/c/LAB/NEW_RULES/08.6.MODAL.md
 */

const PREFIX = 'sharemodal';

const SharedModalContext = createContext(PREFIX);

// Dimensoes minimas por variante (08.6.MODAL.md Regra R5)
// Nota: variante "default" nao impoe min-height — o modal deve crescer com o conteúdo
const VARIANT_DEFAULTS = {
  confirm: { minWidth: 400, minHeight: 250 },
  form: { minWidth: 600, minHeight: 500 },
  default: { minWidth: 500, minHeight: 0 },
  wizard: { minWidth: 700, minHeight: 550 },
  'with-ai': { minWidth: 900, minHeight: 600 }
};

// Contador global para z-index stacking de modais empilhados
let openModalCount = 0;

const SharedModal = ({
  isOpen = false,
  onClose,
  title = '',
  subtitle = '',
  icon,
  variant = 'default',
  size = null,
  sharemodal = 'sharemodal',
  draggable = true,
  resizable = true,
  minWidth: minWidthProp,
  minHeight: minHeightProp,
  showCloseButton = true,
  closeOnOverlay = false,
  closeOnEscape = false,
  buttons = null,
  footerContent = null,
  children,
  className = ''
}) => {
  const modalRef = useRef(null);

  // Dimensoes minimas: prop > variante > default
  const defaults = VARIANT_DEFAULTS[variant] || VARIANT_DEFAULTS.default;
  const minWidth = minWidthProp ?? defaults.minWidth;
  const minHeight = minHeightProp ?? defaults.minHeight;

  // Z-index do overlay (auto-stack para modais empilhados)
  const [overlayZ, setOverlayZ] = useState(1100);

  // Dragging state
  const [isDragging, setIsDragging] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });

  // Resize state
  const [isResizing, setIsResizing] = useState(false);
  const [modalSize, setModalSize] = useState(null);
  const [resizeStart, setResizeStart] = useState({ x: 0, y: 0, w: 0, h: 0 });

  // Z-index stacking: incrementa ao abrir, decrementa ao fechar
  useEffect(() => {
    if (isOpen) {
      openModalCount++;
      setOverlayZ(1100 + (openModalCount - 1) * 200);
      return () => {
        openModalCount = Math.max(0, openModalCount - 1);
      };
    }
  }, [isOpen]);

  // Lock body scroll (so o primeiro modal)
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    }
    return () => {
      if (openModalCount === 0) {
        document.body.style.overflow = 'unset';
      }
    };
  }, [isOpen]);

  // ESC key handler (so se permitido)
  useEffect(() => {
    if (!isOpen || !closeOnEscape) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, closeOnEscape, onClose]);

  // Reset position/size on open, stop animation before drag starts
  useEffect(() => {
    if (isOpen) {
      setPosition({ x: 0, y: 0 });
      setModalSize(null);
      // Remove animation after it completes so it doesn't conflict with drag transform
      const timer = setTimeout(() => {
        const el = modalRef.current;
        if (el) el.style.animation = 'none';
      }, 350);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  // Dragging handlers
  const handleMouseDown = useCallback((e) => {
    if (!draggable) return;
    if (!e.target.closest(`.${sharemodal}__header`)) return; // só o header arrasta
    if (e.target.closest(`.${sharemodal}__close`)) return;   // ✕ fecha, não arrasta
    e.preventDefault();
    setIsDragging(true);
    // Use getBoundingClientRect for accurate offset (accounts for CSS centering + existing translate)
    const rect = modalRef.current?.getBoundingClientRect();
    const startLeft = rect ? rect.left : 0;
    const startTop = rect ? rect.top : 0;
    setDragOffset({
      x: e.clientX - startLeft,
      y: e.clientY - startTop
    });
  }, [draggable, sharemodal]);

  useEffect(() => {
    if (!isDragging) return;
    const handleMouseMove = (e) => {
      // dragOffset = where the user clicked relative to the modal's top-left corner
      // We want the modal's top-left to be at (clientX - dragOffset.x, clientY - dragOffset.y)
      // The overlay centers the modal via flexbox, so translate must offset FROM center
      const targetLeft = e.clientX - dragOffset.x;
      const targetTop = e.clientY - dragOffset.y;
      const centerX = window.innerWidth / 2;
      const centerY = window.innerHeight / 2;
      const rect = modalRef.current?.getBoundingClientRect();
      const halfW = rect ? rect.width / 2 : 0;
      const halfH = rect ? rect.height / 2 : 0;
      const translateX = targetLeft - (centerX - halfW);
      const translateY = targetTop - (centerY - halfH);
      const maxX = window.innerWidth * 0.4;
      const maxY = window.innerHeight * 0.4;
      setPosition({
        x: Math.max(-maxX, Math.min(maxX, translateX)),
        y: Math.max(-maxY, Math.min(maxY, translateY))
      });
    };
    const handleMouseUp = () => setIsDragging(false);
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, dragOffset]);

  // Resize handlers
  const handleResizeMouseDown = useCallback((e) => {
    if (!resizable) return;
    e.preventDefault();
    e.stopPropagation();
    const rect = modalRef.current?.getBoundingClientRect();
    if (!rect) return;
    setIsResizing(true);
    setResizeStart({
      x: e.clientX,
      y: e.clientY,
      w: rect.width,
      h: rect.height
    });
  }, [resizable]);

  useEffect(() => {
    if (!isResizing) return;
    const handleMouseMove = (e) => {
      const newW = Math.max(minWidth, resizeStart.w + (e.clientX - resizeStart.x));
      const newH = Math.max(minHeight, resizeStart.h + (e.clientY - resizeStart.y));
      const maxW = window.innerWidth * 0.95;
      const maxH = window.innerHeight * 0.95;
      setModalSize({
        width: Math.min(newW, maxW),
        height: Math.min(newH, maxH)
      });
    };
    const handleMouseUp = () => setIsResizing(false);
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing, resizeStart, minWidth, minHeight]);

  /**
   * Click no overlay — só fecha o modal quando closeOnOverlay=true E o click foi
   * exactamente no overlay (e.currentTarget), nunca num descendente. Default
   * closeOnOverlay=false: o modal só fecha pelos botões Close/Cancelar/OK/Gravar.
   */
  const handleOverlayClick = (e) => {
    if (!closeOnOverlay) return;
    if (e.target === e.currentTarget) onClose?.();
  };

  if (!isOpen) return null;

  const modalClasses = [
    sharemodal,
    `${sharemodal}--${variant}`,
    draggable && `${sharemodal}--draggable`,
    isDragging && `${sharemodal}--dragging`,
    resizable && `${sharemodal}--resizable`,
    isResizing && `${sharemodal}--resizing`,
    modalSize && `${sharemodal}--user-sized`,
    className
  ].filter(Boolean).join(' ');

  // size={[width, height]} — max-width/max-height com protecao viewport via min()
  // Aceita tambem strings semanticas: 'sm' | 'md' | 'lg'
  const sizeArr = Array.isArray(size)
    ? size
    : size === 'sm' ? [500, 400]
    : size === 'md' ? [700, 500]
    : size === 'lg' ? [900, 600]
    : null;

  const sizeStyle = sizeArr
    ? {
        maxWidth: `min(${sizeArr[0]}px, 95vw)`,
        maxHeight: `min(${sizeArr[1]}px, 90vh)`
      }
    : {};

  const modalStyle = {
    width: '100%',
    ...sizeStyle,
    ...(position.x || position.y ? { transform: `translate(${position.x}px, ${position.y}px)` } : {}),
    ...(modalSize ? { width: `${modalSize.width}px`, height: `${modalSize.height}px`, maxWidth: '95vw !important', maxHeight: '95vh !important' } : {}),
    ...(resizable ? { minWidth: `${minWidth}px`, minHeight: `${minHeight}px` } : {})
  };

  // Footer: buttons (prioritario) ou footerContent (custom)
  const hasFooter = buttons || footerContent;

  const modalElement = (
    <SharedModalContext.Provider value={sharemodal}>
      <div
        className={`${sharemodal}-overlay`}
        onClick={handleOverlayClick}
        style={{ zIndex: overlayZ }}
      >
        <div
          ref={modalRef}
          className={modalClasses}
          style={{ ...modalStyle, zIndex: overlayZ + 100 }}
          role="dialog"
          aria-modal="true"
          aria-label={title}
          onMouseDown={handleMouseDown}
        >
          {/* Header - icon e titulo obrigatorios */}
          <div className={`${sharemodal}__header`}>
            <div className={`${sharemodal}__header-info`}>
              <span className={`${sharemodal}__header-icon`}>
                {icon || 'ℹ'}
              </span>
              <div className={`${sharemodal}__header-text`}>
                <h3>{title || 'Modal'}</h3>
                {subtitle && <p>{subtitle}</p>}
              </div>
            </div>
            {showCloseButton && (
              <button
                className={`${sharemodal}__close`}
                onClick={onClose}
                aria-label="Close modal"
                type="button"
              >
                &#x2715;
              </button>
            )}
          </div>

          {/* Body */}
          <div className={`${sharemodal}__body`}>
            {children}
          </div>

          {/* Footer - botoes dinamicos ou conteudo custom */}
          {hasFooter && (
            <div className={`${sharemodal}__footer`}>
              {buttons ? (
                <div className={`${sharemodal}__footer-actions`}>
                  {buttons.map((btn, i) => {
                    const variant = btn.variant || 'secondary';   // 'primary' | 'secondary' | 'danger' | 'ghost'
                    const cls = 'btn-animated-border btn-sm' + (variant !== 'primary' ? ' btn-' + variant : '');
                    return (
                      <button
                        key={i}
                        type="button"
                        className={cls}
                        data-active={variant === 'primary' ? 'true' : undefined}
                        onClick={btn.onClick}
                        disabled={btn.disabled || false}
                      >
                        {btn.text}
                      </button>
                    );
                  })}
                </div>
              ) : footerContent}
            </div>
          )}

          {/* Resize Handle */}
          {resizable && (
            <div
              className={`${sharemodal}__resize-handle`}
              onMouseDown={handleResizeMouseDown}
            />
          )}
        </div>
      </div>
    </SharedModalContext.Provider>
  );

  // Portal: renderiza no body para z-index correto em modais empilhados
  return createPortal(modalElement, document.body);
};

// ─── Sub-componentes (prefixo via SharedModalContext, fallback 'sharemodal') ───

SharedModal.Content = ({ children, className = '' }) => {
  const p = useContext(SharedModalContext) || 'sharemodal';
  return (
    <div className={`${p}__content ${className}`}>
      {children}
    </div>
  );
};

SharedModal.Form = ({ children, onSubmit, className = '', ...props }) => {
  const p = useContext(SharedModalContext) || 'sharemodal';
  return (
    <form className={`${p}__form ${className}`} onSubmit={onSubmit} {...props}>
      {children}
    </form>
  );
};

SharedModal.Footer = ({ children, className = '' }) => {
  const p = useContext(SharedModalContext) || 'sharemodal';
  return (
    <div className={`${p}__footer-actions ${className}`}>
      {children}
    </div>
  );
};

SharedModal.Group = ({ children, className = '' }) => {
  const p = useContext(SharedModalContext) || 'sharemodal';
  return (
    <div className={`${p}__group ${className}`}>
      {children}
    </div>
  );
};

SharedModal.Label = ({ children, htmlFor, className = '' }) => {
  const p = useContext(SharedModalContext) || 'sharemodal';
  return (
    <label className={`${p}__label ${className}`} htmlFor={htmlFor}>
      {children}
    </label>
  );
};

SharedModal.Input = ({ className = '', ...props }) => {
  const p = useContext(SharedModalContext) || 'sharemodal';
  return <input className={`${p}__input ${className}`} {...props} />;
};

SharedModal.Textarea = ({ className = '', ...props }) => {
  const p = useContext(SharedModalContext) || 'sharemodal';
  return <textarea className={`${p}__textarea ${className}`} {...props} />;
};

SharedModal.Select = ({ children, className = '', ...props }) => {
  const p = useContext(SharedModalContext) || 'sharemodal';
  return (
    <select className={`${p}__select ${className}`} {...props}>
      {children}
    </select>
  );
};

SharedModal.Error = ({ children, className = '' }) => {
  const p = useContext(SharedModalContext) || 'sharemodal';
  return <span className={`${p}__error ${className}`}>{children}</span>;
};

SharedModal.Hint = ({ children, className = '' }) => {
  const p = useContext(SharedModalContext) || 'sharemodal';
  return <span className={`${p}__hint ${className}`}>{children}</span>;
};

SharedModal.Row = ({ children, className = '' }) => {
  const p = useContext(SharedModalContext) || 'sharemodal';
  return <div className={`${p}__row ${className}`}>{children}</div>;
};

SharedModal.AiButton = ({ children, onClick, className = '' }) => {
  const p = useContext(SharedModalContext) || 'sharemodal';
  return (
    <button
      type="button"
      className={`${p}__ai-btn ${className}`}
      onClick={onClick}
    >
      {children}
    </button>
  );
};

SharedModal.InfoBox = ({ children, icon = null, className = '' }) => {
  const p = useContext(SharedModalContext) || 'sharemodal';
  return (
    <div className={`${p}__info-box ${className}`}>
      {icon}
      <span>{children}</span>
    </div>
  );
};

SharedModal.Spinner = ({ className = '' }) => {
  const p = useContext(SharedModalContext) || 'sharemodal';
  return <span className={`${p}__spinner ${className}`} />;
};

SharedModal.AiSidebar = ({ children, title = 'Ollama AI', logo = null, className = '' }) => {
  const p = useContext(SharedModalContext) || 'sharemodal';
  return (
    <div className={`${p}__ai-sidebar ${className}`}>
      <div className={`${p}__ai-sidebar-header`}>
        {logo && <img className={`${p}__ai-sidebar-logo`} src={logo} alt="AI" />}
        <h4 className={`${p}__ai-sidebar-title`}>{title}</h4>
      </div>
      <div className={`${p}__ai-sidebar-content`}>
        {children}
      </div>
      <div className={`${p}__ai-sidebar-footer`}>
        <p className={`${p}__ai-sidebar-footer-text`}>Powered by Ollama</p>
      </div>
    </div>
  );
};

SharedModal.FormContainer = ({ children, className = '' }) => {
  const p = useContext(SharedModalContext) || 'sharemodal';
  return (
    <div className={`${p}__form-container ${className}`}>
      {children}
    </div>
  );
};

SharedModal.Progress = ({ children, className = '' }) => {
  const p = useContext(SharedModalContext) || 'sharemodal';
  return <div className={`${p}__progress ${className}`}>{children}</div>;
};

SharedModal.Step = ({ children, active = false, completed = false, icon: stepIcon, label = '' }) => {
  const p = useContext(SharedModalContext) || 'sharemodal';
  const classes = [
    `${p}__step`,
    active && `${p}__step--active`,
    completed && `${p}__step--completed`
  ].filter(Boolean).join(' ');

  return (
    <div className={classes}>
      <div className={`${p}__step-icon`}>
        {completed ? '✓' : stepIcon}
      </div>
      {label && <span className={`${p}__step-label`}>{label}</span>}
    </div>
  );
};

SharedModal.StepContent = ({ children, title: stepTitle, icon: stepIcon, className = '' }) => {
  const p = useContext(SharedModalContext) || 'sharemodal';
  return (
    <div className={`${p}__step-content ${className}`}>
      {stepTitle && (
        <h4>
          {stepIcon}
          {stepTitle}
        </h4>
      )}
      {children}
    </div>
  );
};

SharedModal.ConfirmIcon = ({ type = 'danger', children, className = '' }) => {
  const p = useContext(SharedModalContext) || 'sharemodal';
  return (
    <div className={`${p}__confirm-icon ${p}__confirm-icon--${type} ${className}`}>
      {children}
    </div>
  );
};

SharedModal.ConfirmTitle = ({ children, className = '' }) => {
  const p = useContext(SharedModalContext) || 'sharemodal';
  return <h3 className={`${p}__confirm-title ${className}`}>{children}</h3>;
};

SharedModal.ConfirmMessage = ({ children, className = '' }) => {
  const p = useContext(SharedModalContext) || 'sharemodal';
  return <p className={`${p}__confirm-message ${className}`}>{children}</p>;
};

SharedModal.Options = ({ children, className = '' }) => {
  const p = useContext(SharedModalContext) || 'sharemodal';
  return <div className={`${p}__options ${className}`}>{children}</div>;
};

SharedModal.Option = ({ children, className = '', ...props }) => {
  const p = useContext(SharedModalContext) || 'sharemodal';
  return (
    <div className={`${p}__option ${className}`} {...props}>
      {children}
    </div>
  );
};

SharedModal.Channels = ({ children, className = '' }) => {
  const p = useContext(SharedModalContext) || 'sharemodal';
  return <div className={`${p}__channels ${className}`}>{children}</div>;
};

SharedModal.Channel = ({ children, className = '', ...props }) => {
  const p = useContext(SharedModalContext) || 'sharemodal';
  return (
    <div className={`${p}__channel ${className}`} {...props}>
      {children}
    </div>
  );
};

SharedModal.Tags = ({ children, className = '' }) => {
  const p = useContext(SharedModalContext) || 'sharemodal';
  return <div className={`${p}__tags ${className}`}>{children}</div>;
};

SharedModal.Tag = ({ children, className = '' }) => {
  const p = useContext(SharedModalContext) || 'sharemodal';
  return <span className={`${p}__tag ${className}`}>{children}</span>;
};

export default SharedModal;
